/**
 * `useAdminSession` — hidrata `/api/auth/me` y, en paralelo, intenta
 * `/api/admin/dashboard` para inferir si el user tiene rol admin/support.
 *
 * El backend no expone `role` en `/me` (PublicUser shape antiguo). El gate
 * efectivo es la respuesta de cualquier endpoint admin: si responde 200 el
 * user tiene rol válido; 403 → no admin; 401 → no logueado.
 *
 * Cualquier OTRO código (404, 5xx) NO es un veredicto sobre el rol: es el
 * árbol admin caído o apagado. Antes se traducía a `forbidden` "por
 * conservador" y la pantalla acusaba al rol del user de un problema del
 * backend — 2026-09-02 un preview con `NODE_ENV=production` y sin
 * `ADMIN_ENABLED` desmontaba `/api/admin/*`, cada ruta daba 404, y un
 * usuario con `role='admin'` en la DB leía "no tiene rol admin o support".
 * Sigue sin dar acceso (eso lo decide el backend), pero dice la verdad.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchMe, type MeUser, AdminApiError } from '../api/admin.js';

export type AdminAuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'forbidden'; user: MeUser }
  | { status: 'unavailable'; user: MeUser; httpStatus: number }
  | { status: 'admin'; user: MeUser };

type ProbeResult =
  | { user: MeUser }
  | { user: MeUser; forbidden: true }
  | { user: MeUser; unavailable: number }
  | null;

async function probeAdminAccess(): Promise<ProbeResult> {
  const user = await fetchMe();
  if (!user) return null;
  try {
    const res = await fetch('/api/admin/dashboard', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) return { user };
    if (res.status === 403) return { user, forbidden: true };
    if (res.status === 401) return null;
    return { user, unavailable: res.status };
  } catch (err) {
    if (err instanceof AdminApiError && err.status === 403) {
      return { user, forbidden: true };
    }
    throw err;
  }
}

export function useAdminSession(): AdminAuthState {
  const query = useQuery({
    queryKey: ['admin-session'],
    queryFn: probeAdminAccess,
    staleTime: 60_000,
    retry: false,
  });

  if (query.isPending) return { status: 'loading' };
  if (query.isError) return { status: 'anonymous' };
  const data = query.data;
  if (!data) return { status: 'anonymous' };
  if ('forbidden' in data && data.forbidden) return { status: 'forbidden', user: data.user };
  if ('unavailable' in data) {
    return { status: 'unavailable', user: data.user, httpStatus: data.unavailable };
  }
  return { status: 'admin', user: data.user };
}
